import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  useToast,
  Flex,
  Image,
  Center,
  Heading,
  useColorModeValue,
  Spinner,
  Link,
  HStack,
  InputGroup,
  InputRightElement,
  IconButton,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useAuth } from "../contexts/AuthContext";
import { authService } from "../lib/authService";
import { useTranslation } from 'react-i18next';
import bgImage from '../assets/images/HansCard_L.png';
import LogoWithBackground from '../components/LogoWithBackground';
import logger from '../utils/logger';

export default function ChangePassword() {
  // State
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  // Check for hash fragment containing auth tokens on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsAuthenticating(true);

        if (user) {
          setIsSuccess(true);
        } else {
          // Try to verify session from API
          const session = await apiClient.getSession();
          if (session && session.user) {
            setIsSuccess(true);
          } else {
            setAuthError(
              "No active session. Please log in to change your password.",
            );
          }
        }
      } catch (err) {
        logger.error("Error in auth check:", err);
        setAuthError("An error occurred during authentication.");
      } finally {
        setIsAuthenticating(false);
      }
    };

    checkAuth();
  }, [user]);

  // Form submission handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      await authService.updatePassword(password);

      setIsSuccess(true);
      toast({
        title: "Password updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Redirect after short delay
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    } catch (error) {
      toast({
        title: 'Error updating password',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Theme values
  const formBgColor = useColorModeValue(
    'rgba(255, 255, 255, 0.85)',
    `rgba(34, 35, 57, 0.85)`
  );
  const borderColor = useColorModeValue('gray.200', 'rgba(255, 255, 255, 0.1)');
  const shadowColor = useColorModeValue(
    '0 4px 20px rgba(0, 0, 0, 0.1)',
    '0 4px 20px rgba(0, 0, 0, 0.3)'
  );
  const backdropBgColor = useColorModeValue(
    'rgba(255, 255, 255, 0.4)',
    'rgba(0, 0, 0, 0.4)'
  );
  const logoFilter = useColorModeValue('none', 'brightness(1.2)');
  const inputBgColor = useColorModeValue('white', 'rgba(45, 55, 72, 0.5)');
  const inputFocusBgColor = useColorModeValue('white', 'rgba(45, 55, 72, 0.8)');
  const hoverBoxShadow = useColorModeValue(
    '0 8px 30px rgba(0, 0, 0, 0.12)',
    '0 8px 30px rgba(0, 0, 0, 0.4)'
  );

  return (
    <Box
      minHeight="100vh"
      width="100%"
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      backgroundImage={`url(${bgImage})`}
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundRepeat="no-repeat"
      overflow="hidden"
    >
      <Flex
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        alignItems="center"
        justifyContent="center"
        backdropFilter="blur(5px)"
        bg={backdropBgColor}
      >
        <Container
          maxW="md"
          py={10}
          px={4}
          position="relative"
          zIndex={1}
          mx="auto"
          centerContent
        >
          <Center mb={8} w="full">
            <LogoWithBackground
              width={{ base: "200px", md: "250px" }}
              objectFit="contain"
              transition="filter 0.2s ease"
            />
          </Center>

          <Box
            p={{ base: 6, md: 8 }}
            width="100%"
            bg={formBgColor}
            boxShadow={shadowColor}
            borderRadius="xl"
            border="1px"
            borderColor={borderColor}
            backdropFilter="blur(10px)"
            transition="all 0.3s ease"
            _hover={{
              boxShadow: hoverBoxShadow,
              transform: 'translateY(-2px)'
            }}
          >
            <VStack spacing={6}>
              <Heading as="h1" size="lg" textAlign="center">
                Change Password
              </Heading>

              {isAuthenticating ? (
                <VStack spacing={4} py={4}>
                  <Spinner size="xl" thickness="4px" color="brand.500" />
                  <Text>Verifying your request...</Text>
                </VStack>
              ) : authError ? (
                <VStack spacing={4}>
                  <Text textAlign="center" color="red.500">
                    {authError}
                  </Text>
                  <Button
                    colorScheme="brand"
                    onClick={() => navigate('/forgot-password')}
                    width="full"
                  >
                    Try Again
                  </Button>
                </VStack>
              ) : isSuccess ? (
                <VStack spacing={4}>
                  <Text textAlign="center">
                    Your password has been updated successfully!
                  </Text>
                  <Button
                    colorScheme="brand"
                    onClick={() => navigate('/login')}
                    width="full"
                  >
                    Go to Login
                  </Button>
                </VStack>
              ) : (
                <VStack as="form" onSubmit={handleSubmit} spacing={4} width="full">
                  <FormControl id="password" isRequired>
                    <FormLabel>New Password</FormLabel>
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      bg={inputBgColor}
                      _focus={{
                        bg: inputFocusBgColor,
                        borderColor: 'brand.500'
                      }}
                    />
                  </FormControl>

                  <FormControl id="confirmPassword" isRequired>
                    <FormLabel>Confirm New Password</FormLabel>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      bg={inputBgColor}
                      _focus={{
                        bg: inputFocusBgColor,
                        borderColor: 'brand.500'
                      }}
                    />
                  </FormControl>

                  <Button
                    type="submit"
                    colorScheme="brand"
                    width="full"
                    isLoading={isLoading}
                    mt={4}
                  >
                    Update Password
                  </Button>
                </VStack>
              )}
            </VStack>
          </Box>
        </Container>
      </Flex>
    </Box>
  );
}